import { writable, get } from 'svelte/store';
import {
  sendPassword,
  login,
  getAuthorizedCounterparty,
  getOrders,
  logoutCounterparty
} from '../services/gigmaApi.js';
import { isOrderPaid } from '../sections/account-ticket.js';
import {
  readProfileSyncFailure,
  clearProfileSyncFailure
} from '../utils/profileSync.js';

const TOKEN_KEY = 'forum-industry.account-token';

function readToken() {
  if (typeof sessionStorage === 'undefined') return '';
  try {
    return sessionStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

function writeToken(value) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (value) sessionStorage.setItem(TOKEN_KEY, value);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {}
}

// step: 'idle' | 'login' | 'verify' | 'loading' | 'ready'
// Инициализация: если в sessionStorage уже есть токен — стартуем в 'loading',
// чтобы AccountPage показал спиннер вместо мгновенного flash формы входа
// у уже залогиненного юзера. hydrate() позже переключит на 'ready' или
// 'login' (если токен истёк).
const initialToken = readToken();
export const step = writable(initialToken ? 'loading' : 'idle');
// Инициализируем токен сразу из sessionStorage, чтобы Header (и другие
// подписчики) знали об активной сессии при первом рендере, ещё до hydrate().
export const token = writable(initialToken);

// Публичный helper для других потоков (например, после verifyCode в registration.js):
// сохраняем токен в той же ячейке, что использует hydrate() ЛК.
// Обновляем и sessionStorage, и реактивный store — чтобы Header и другие
// подписчики увидели сессию сразу, даже если редиректа на YooKassa не было
// (paymentLink === null).
export function persistAccountToken(value) {
  token.set(value);
  writeToken(value);
}
export const profile = writable(null);
export const orders = writable([]);
export const loginEmail = writable('');
export const loginCode = writable('');
export const isSubmitting = writable(false);
export const isResending = writable(false);
export const formError = writable('');
export const codeError = writable('');
export const ordersError = writable('');
export const isLoggingOut = writable(false);

// Видимость non-blocking PUT /counterparty: registration.js пишет причину
// в sessionStorage через recordProfileSyncFailure(), здесь поднимаем
// реактивный store для AccountProfileSection. Значение = { reason, ts } | null.
// Изначально читаем из sessionStorage — при возврате с YooKassa /account
// инициализируется заново, и баннер сразу подхватится.
export const profileSyncFailure = writable(readProfileSyncFailure());

export function dismissProfileSyncWarning() {
  clearProfileSyncFailure();
  profileSyncFailure.set(null);
}

// Polling после возврата с YooKassa. Бэк webhook payment.succeeded меняет
// order_status_id на IS_PAID асинхронно — обычно за 1-3 сек, иногда дольше.
// Фронт делает несколько повторных getOrders, чтобы статус подтянулся
// автоматически без ручного обновления страницы.
export const paymentPolling = writable(false);
export const paymentTimedOut = writable(false);

export function setLoginEmail(value) {
  loginEmail.set(value);
  formError.set('');
}

export function setLoginCode(value) {
  loginCode.set(value.replace(/\D/g, ''));
  codeError.set('');
}

function applyToken(value) {
  token.set(value);
  writeToken(value);
}

function sanitizeMessage(raw) {
  return String(raw ?? '').replace(/Bearer [\w.\-]+/g, 'Bearer [REDACTED]');
}

async function loadProfileAndOrders(bearer) {
  // getAuthorizedCounterparty критичен — без него ЛК пуст.
  // getOrders может частично упасть (например 500): профиль покажем, для
  // заказов оставим маркер, чтобы UI мог показать «не удалось загрузить заказы».
  const user = await getAuthorizedCounterparty(bearer);
  profile.set(user?.counterparty ?? user ?? null);
  await refreshOrdersInternal(bearer);
}

async function refreshOrdersInternal(bearer) {
  try {
    const ordersRes = await getOrders(bearer);
    orders.set(ordersRes?.orders ?? []);
    ordersError.set('');
    return ordersRes?.orders ?? [];
  } catch (e) {
    console.warn('[getOrders] failed:', sanitizeMessage(e?.message));
    orders.set([]);
    ordersError.set('Не удалось загрузить заказы. Обновите страницу.');
    return [];
  }
}

/**
 * Отдельная функция для повторной подкачки только заказов (без профиля).
 * Используется polling'ом после возврата с YooKassa.
 */
export async function refreshOrders() {
  const bearer = get(token);
  if (!bearer) return [];
  return refreshOrdersInternal(bearer);
}

/**
 * Polling статуса оплаты после возврата с YooKassa (success_payment_url).
 * Webhook от платёжки приходит асинхронно; делаем 8 попыток по 2 сек.
 *
 * Ждём перехода в IS_PAID именно для заказов, по которым юзер запустил
 * оплату (targetOrderIds). Раньше проверяли «есть ли любой paid в списке» —
 * это давало false positive: если у юзера старый paid + новый pending,
 * polling видел уже paid (старый) и останавливался, а новый pending
 * оставался стейл.
 */
let pollTimerId = null;
let pollAttempts = 0;
let targetOrderIds = [];
const POLL_MAX = 8;
const POLL_INTERVAL_MS = 2000;

export function startPaymentPolling(orderIds = []) {
  if (typeof window === 'undefined') return;
  if (get(paymentPolling)) return; // уже идёт
  if (!get(token)) return;
  targetOrderIds = Array.isArray(orderIds) ? orderIds.filter(Number.isFinite) : [];
  if (targetOrderIds.length === 0) return; // некого ждать
  paymentPolling.set(true);
  paymentTimedOut.set(false);
  pollAttempts = 0;
  schedulePollTick();
}

export function stopPaymentPolling() {
  if (pollTimerId != null) {
    clearTimeout(pollTimerId);
    pollTimerId = null;
  }
  targetOrderIds = [];
  paymentPolling.set(false);
}

function schedulePollTick() {
  pollTimerId = setTimeout(async () => {
    pollAttempts += 1;
    const list = await refreshOrders();
    // Все таргет-заказы должны стать paid — иначе ждём дальше.
    const allTargetsPaid =
      Array.isArray(list) &&
      targetOrderIds.length > 0 &&
      targetOrderIds.every((id) => {
        const order = list.find((o) => o.id === id);
        return order && isOrderPaid(order);
      });
    if (allTargetsPaid) {
      stopPaymentPolling();
      return;
    }
    if (pollAttempts >= POLL_MAX) {
      paymentTimedOut.set(true);
      stopPaymentPolling();
      return;
    }
    schedulePollTick();
  }, POLL_INTERVAL_MS);
}

function mapAuthFlowError(e) {
  if (e?.endpoint === 'login' && (e?.httpStatus === 400 || e?.httpStatus === 401)) {
    return 'Неверный код. Проверьте письмо и попробуйте снова.';
  }
  const msg = e?.message ?? '';
  if (msg.includes('Send password before login') || msg.includes('Password TTL')) {
    return 'Срок действия кода истёк. Нажмите «Отправить повторно».';
  }
  if (e?.httpStatus >= 500) return 'Сервер временно недоступен. Попробуйте позже.';
  return 'Что-то пошло не так. Попробуйте ещё раз.';
}

function mapRequestCodeError(e) {
  if (e?.kind === 'auth') return 'Не удалось авторизоваться. Проверьте email.';
  if (e?.httpStatus >= 500) return 'Сервер временно недоступен. Попробуйте позже.';
  return e?.message || 'Не удалось отправить код. Попробуйте позже.';
}

export async function hydrate() {
  const saved = readToken();
  if (!saved) {
    step.set('login');
    return;
  }
  step.set('loading');
  applyToken(saved);
  // Перечитываем флаг из sessionStorage — verifyCode мог записать его
  // перед редиректом на YooKassa, и при возврате на /account стор инициализируется
  // ещё ДО hydrate (на старте модуля). Этот set — страховка на случай повторного
  // hydrate в рамках одной загрузки страницы.
  profileSyncFailure.set(readProfileSyncFailure());
  try {
    await loadProfileAndOrders(saved);
    step.set('ready');
  } catch (e) {
    if (e?.kind === 'auth' || e?.httpStatus === 401) {
      applyToken('');
      profile.set(null);
      orders.set([]);
      ordersError.set('');
      step.set('login');
      formError.set('Сессия истекла. Войдите снова.');
    } else {
      step.set('login');
      formError.set('Не удалось загрузить кабинет. Попробуйте позже.');
    }
  }
}

export async function requestLoginCode() {
  const email = get(loginEmail).trim();
  if (!email || !/\S+@\S+\.\S+/.test(email)) {
    formError.set('Укажите корректный email');
    return;
  }
  isSubmitting.set(true);
  formError.set('');
  try {
    await sendPassword(email);
    step.set('verify');
    loginCode.set('');
    codeError.set('');
  } catch (e) {
    formError.set(mapRequestCodeError(e));
  } finally {
    isSubmitting.set(false);
  }
}

export async function resendLoginCode() {
  const email = get(loginEmail).trim();
  if (!email) return;
  isResending.set(true);
  codeError.set('');
  try {
    await sendPassword(email);
    codeError.set('Новый код отправлен на ваш email.');
  } catch {
    codeError.set('Не удалось отправить код. Попробуйте позже.');
  } finally {
    isResending.set(false);
  }
}

export async function verifyLoginCode() {
  const code = get(loginCode).trim();
  const email = get(loginEmail).trim();
  if (!code) {
    codeError.set('Введите код из письма');
    return;
  }
  isSubmitting.set(true);
  codeError.set('');
  try {
    const bearer = await login(email, code);
    applyToken(bearer);
    step.set('loading');
    await loadProfileAndOrders(bearer);
    step.set('ready');
  } catch (e) {
    codeError.set(mapAuthFlowError(e));
  } finally {
    isSubmitting.set(false);
  }
}

export function backToLogin() {
  step.set('login');
  loginCode.set('');
  codeError.set('');
}

export async function logout() {
  // Защита от двойного клика «Выйти»: два POST /counterparty/logout не нужны.
  if (get(isLoggingOut)) return;
  isLoggingOut.set(true);
  try {
    const bearer = get(token);
    if (bearer) {
      try {
        await logoutCounterparty(bearer);
      } catch (e) {
        console.warn('[logout] backend failed (non-blocking):', sanitizeMessage(e?.message));
      }
    }
    stopPaymentPolling();
    applyToken('');
    profile.set(null);
    orders.set([]);
    loginEmail.set('');
    loginCode.set('');
    formError.set('');
    codeError.set('');
    paymentTimedOut.set(false);
    // Сбрасываем баннер «профиль не сохранён» при выходе — он привязан
    // к конкретной сессии регистрации, для следующего юзера в этой
    // вкладке нерелевантен.
    clearProfileSyncFailure();
    profileSyncFailure.set(null);
    step.set('login');
  } finally {
    isLoggingOut.set(false);
  }
}
